// Get selected Vertical IDs based on the filter type (L1Vertical or L2Vertical)
// Parameters:
// - data: the filter data object containing L1Vertical, L2Vertical, or L3Vertical selections
// - verticalType: the key used to identify vertical type ('L1Vertical', 'L2Vertical', etc.)
const getSelectedVerticalIds = (data, verticalType) => {
  try {
    // Check if data for the specified verticalType exists and is in array format
    if (data && data[verticalType] && Array.isArray(data[verticalType].$in)) {
      return data[verticalType].$in.map(id => id.match(new RegExp(`Enum.${verticalType}'(.*)'`))[1]);
    }

    // Handle case where the data uses a $and condition (multiple filters)
    if (data && Array.isArray(data.$and)) {
      return data.$and
        .filter(f => f[verticalType] && Array.isArray(f[verticalType].$in))
        .flatMap(f => f[verticalType].$in)
        .map(id => id.match(new RegExp(`Enum.${verticalType}'(.*)'`))[1]);
    }
  } catch (error) {
    console.error(`Error in getting selected vertical IDs for ${verticalType}:`, error);
  }

  return []; // Return empty array if no valid data found
};

// Filter vertical IDs based on the selected verticals
// Parameters:
// - entitySchema: the schema of the entity containing vertical options
// - reportSchemaFilters: the schema filters for the report
// - selectedIds: array of selected vertical IDs (L1 or L2)
// - verticalKey: the key used to identify the vertical (e.g., 'L2Vertical' or 'L3Vertical')
const filterVerticalIds = (entitySchema, reportSchemaFilters, selectedIds, verticalKey) => {
  try {
    // Ensure necessary properties are present in the entitySchema and reportSchemaFilters
    if (
      entitySchema &&
      entitySchema.properties &&
      entitySchema.properties[verticalKey] &&
      Array.isArray(entitySchema.properties[verticalKey].enum) &&
      selectedIds.length > 0
    ) {
      // Filter the enum values based on the selected IDs
      return reportSchemaFilters.properties[verticalKey].enum.filter(id =>
        selectedIds.some(selectedId => id.startsWith(`${selectedId}/`)));
    }
  } catch (error) {
    console.error(`Error filtering vertical IDs for ${verticalKey}:`, error);
  }

  // Return original enum if no selected IDs or if an error occurs
  return reportSchemaFilters.properties[verticalKey].enum;
};

// Dynamically set and trim filter options for L2 and L3 verticals based on selected L1 vertical
// Parameters:
// - filterData: the current filter data object
// - reportSchemaFilters: schema filters for the report
// - entitySchema: the current entity schema being updated
// - setEntitySchema: function to update the entity schema
export const setTrimmedFilterDataOptionsSearchInsightsReport = (filterData, reportSchemaFilters, entitySchema, setEntitySchema) => {
  try {
    // Get selected L1 and L2 vertical IDs from the filter data
    const selectedL1VerticalIds = getSelectedVerticalIds(filterData, 'L1Vertical');
    const selectedL2VerticalIds = getSelectedVerticalIds(filterData, 'L2Vertical');

    // Create a deep copy of the entity schema to modify
    let updatedEntitySchema = { ...entitySchema, properties: { ...entitySchema.properties } };

    // If no L1Vertical is selected, set L2Vertical and L3Vertical to the full list of options
    if (selectedL1VerticalIds.length === 0) {
      updatedEntitySchema = { ...entitySchema, properties: { ...entitySchema.properties } };
      if (reportSchemaFilters.properties.L2Vertical && Array.isArray(reportSchemaFilters.properties.L2Vertical.enum)) {
        updatedEntitySchema.properties.L2Vertical.enum = [...reportSchemaFilters.properties.L2Vertical.enum];
      }
      if (reportSchemaFilters.properties.L3Vertical && Array.isArray(reportSchemaFilters.properties.L3Vertical.enum)) {
        updatedEntitySchema.properties.L3Vertical.enum = [...reportSchemaFilters.properties.L3Vertical.enum];
      }
    } else {
      // Trim L2Vertical options based on the selected L1Vertical IDs
      const trimmedL2Options = filterVerticalIds(entitySchema, reportSchemaFilters, selectedL1VerticalIds, 'L2Vertical');
      updatedEntitySchema.properties.L2Vertical.enum = trimmedL2Options;

      // Trim L3Vertical options based on the selected L2Vertical IDs
      const trimmedL3Options = filterVerticalIds(entitySchema, reportSchemaFilters, selectedL2VerticalIds, 'L3Vertical');
      updatedEntitySchema.properties.L3Vertical.enum = trimmedL3Options;
    }

    // Update the entity schema with the new trimmed options
    setEntitySchema(updatedEntitySchema);
  } catch (error) {
    console.error('Error setting trimmed filter data options:', error);
    setEntitySchema(entitySchema); // Keep the entity schema unchanged in case of error
  }
};

// Set the default L2Vertical options based on selected L1Vertical and update entity schema
// Parameters:
// - filterData: the current filter data object
// - entitySchema: the current entity schema being updated
export const setTrimmedFilterDataOptionsDefaultSearchInsightsReport = (filterData, entitySchema) => {
  try {
    // Get selected L1 vertical IDs from the filter data
    const selectedL1VerticalIds = getSelectedVerticalIds(filterData, 'L1Vertical');

    let trimmedL2Options;

    // If L2Vertical enum exists and there are selected L1 vertical IDs, filter L2 options
    if (
      entitySchema &&
      entitySchema.properties &&
      entitySchema.properties.L2Vertical &&
      Array.isArray(entitySchema.properties.L2Vertical.enum) &&
      selectedL1VerticalIds.length > 0
    ) {
      trimmedL2Options = entitySchema.properties.L2Vertical.enum.filter((l2VerticalId) => {
        const cleanedL2VerticalId = l2VerticalId.replace("Enum.L2Vertical'", '').replace("'", '');
        return selectedL1VerticalIds.some(l1VerticalId => cleanedL2VerticalId.startsWith(`${l1VerticalId}/`));
      });
    }

    // Create a deep copy of the entity schema and update with the trimmed L2 options
    const updatedEntitySchema = { ...entitySchema, properties: { ...entitySchema.properties } };
    if (updatedEntitySchema.properties.L2Vertical && trimmedL2Options) {
      updatedEntitySchema.properties.L2Vertical.enum = trimmedL2Options;
    }

    return updatedEntitySchema;
  } catch (error) {
    console.error('Error setting default trimmed filter data options:', error);
    return entitySchema; // Return original entitySchema if error occurs
  }
};
